import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAccessControl } from '@/hooks/useAccessControl';
import { UpgradePrompt, SubscriptionBlocked } from './UpgradePrompt';
import type { Feature } from '@/config/plans';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  feature: Feature;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

/**
 * Componente para proteger rotas baseado em planos
 * 
 * @example
 * ```tsx
 * <ProtectedRoute feature="reports">
 *   <RelatoriosPage />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  feature,
  fallback,
  redirectTo,
}) => {
  const { checkFeatureAccess, isLoading, getCurrentPlan } = useAccessControl();
  const location = useLocation();

  // Mostra loading enquanto carrega perfil
  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted-foreground">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  const accessResult = checkFeatureAccess(feature);
  const currentPlan = getCurrentPlan();

  // Se tem acesso, renderiza o conteúdo
  if (accessResult.hasAccess) {
    return <>{children}</>;
  }

  // Se deve redirecionar
  if (redirectTo) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Se tem fallback customizado
  if (fallback) {
    return <>{fallback}</>;
  }

  // Mostra bloqueio por assinatura inativa
  if (accessResult.subscriptionInactive && currentPlan) {
    return (
      <div className="container max-w-4xl mx-auto py-12 px-4">
        <SubscriptionBlocked
          status={currentPlan.status as 'late' | 'canceled'}
          message={accessResult.reason}
        />
      </div>
    );
  }

  // Mostra prompt de upgrade
  if (accessResult.upgradeRequired && accessResult.minimumPlan) {
    return (
      <div className="container max-w-4xl mx-auto py-12 px-4">
        <UpgradePrompt
          feature={feature}
          minimumPlan={accessResult.minimumPlan}
          currentPlan={currentPlan?.plan}
          reason={accessResult.reason}
        />
      </div>
    );
  }

  // Fallback genérico
  return (
    <div className="container max-w-4xl mx-auto py-12 px-4">
      <div className="text-center space-y-4">
        <h2 className="text-2xl font-bold">Acesso Negado</h2>
        <p className="text-muted-foreground">
          {accessResult.reason || 'Você não tem permissão para acessar esta página.'}
        </p>
      </div>
    </div>
  );
};

/**
 * HOC para proteger componentes baseado em features
 * 
 * @example
 * ```tsx
 * const ProtectedReports = withFeatureAccess(RelatoriosPage, 'reports');
 * ```
 */
export function withFeatureAccess<P extends object>(
  Component: React.ComponentType<P>,
  feature: Feature,
  options?: {
    fallback?: React.ReactNode;
    redirectTo?: string;
  }
) {
  return function ProtectedComponent(props: P) {
    return (
      <ProtectedRoute 
        feature={feature} 
        fallback={options?.fallback}
        redirectTo={options?.redirectTo}
      >
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}
